import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight } from "lucide-react";
import { ProductCard } from "@/components/Featured";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";

const TITLE = "Fast Food Packaging Kampala | MUBFAR500";
const DESCRIPTION =
  "Chips boxes, burger clamshells, pizza boxes and carriers for Kampala's fast food joints, cafes and food trucks — food-grade, printed with your brand.";

const AUDIENCES = [
  {
    title: "Chips joints & street food",
    body: "Open-top chips boxes, clamshell burger boxes and snack scoops that keep food crisp from fryer to customer — printed with your name so every pack advertises the joint.",
  },
  {
    title: "Cafes & restaurants",
    body: "Pizza boxes, cake boxes and full-print burger boxes that carry your brand home with every order. Food-grade board, your sizes, your artwork.",
  },
  {
    title: "Food trucks & events",
    body: "Cup carriers, bottle carriers and round takeaway boxes built for one-hand service and fast queues — light to stock, quick to serve, hard to spill.",
  },
];

const FAMILY_CATS = ["burger", "pizza", "takeaway", "juice"];

export default function FastFood() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");

    document.title = TITLE;
    if (meta) meta.setAttribute("content", DESCRIPTION);
    if (canonical) canonical.setAttribute("href", "https://mubfar500.com/fast-food");

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
      if (canonical && prevCanonical != null) canonical.setAttribute("href", prevCanonical);
    };
  }, []);

  const lineUp = useMemo(
    () => products.filter((p) => FAMILY_CATS.includes(p.category)),
    [products]
  );

  return (
    <main className="pt-32 pb-24 min-h-screen" data-testid="fastfood-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-mintsoft/90 mb-4">
            Fast Food Packaging — Kampala
          </p>
          <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-6xl text-kraft mb-4">
            Packaging for fast food joints
          </h1>
          <p className="max-w-xl text-kraft/60 mb-14">
            Chips joints, cafes and food trucks run on packaging that survives the ride.
            Every box below is food-grade, cut to your size and printed with your brand —
            quoted on volume, never a fixed price list.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div
                className="h-full rounded-2xl border border-mint/15 bg-moss/30 p-6 sm:p-7"
                data-testid={`fastfood-audience-${i}`}
              >
                <p className="font-mono text-[10px] tracking-[0.25em] text-mint/80 uppercase mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="font-display font-semibold text-xl text-kraft mb-2">{a.title}</h2>
                <p className="text-sm text-kraft/60 leading-relaxed">{a.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <h2 className="font-display font-semibold tracking-tight text-2xl sm:text-4xl text-kraft mb-8">
            The fast food line-up
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="fastfood-grid">
          {lineUp.map((p, i) => (
            <ProductCard key={p.id || p.slug} p={p} index={i} />
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            className="mt-16 rounded-2xl border border-mint/20 bg-moss/30 p-8 sm:p-10 text-center"
            data-testid="fastfood-cta"
          >
            <h2 className="font-display font-semibold text-2xl sm:text-3xl text-kraft mb-3">
              Feeding Kampala's queues?
            </h2>
            <p className="text-sm text-kraft/60 max-w-md mx-auto mb-8">
              Send a free quote request with your sizes and artwork — we call you back
              with a price per box, or talk to us on WhatsApp right now.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/quote"
                data-testid="fastfood-cta-quote"
                className="inline-flex items-center gap-2 rounded-full bg-mint text-forest font-mono text-[11px] tracking-[0.15em] uppercase font-semibold px-6 h-11 hover:bg-mintsoft transition-colors"
              >
                Start a quote <ArrowRight size={14} />
              </Link>
              <a
                href="https://wa.me/256781928239?text=Hello%20MUBFAR500%2C%20I%20need%20packaging%20for%20my%20fast%20food%20business."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fastfood-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-mint/40 text-mint font-mono text-[11px] tracking-[0.15em] uppercase px-6 h-11 hover:bg-mint/10 transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp us
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
